import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Mail, KeyRound, Loader2, ArrowLeft, ShieldCheck, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import { apiRequestPasswordReset, apiConfirmPasswordReset } from '../lib/api';
import logo from '../assets/logo-tecnologie.webp';

export default function ForgotPassword() {
  const nav = useNavigate();
  const [step, setStep] = useState(1); // 1 = request email, 2 = enter code + new pwd, 3 = success
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [newPwd, setNewPwd] = useState('');
  const [confirmPwd, setConfirmPwd] = useState('');
  const [loading, setLoading] = useState(false);
  const [demoCode, setDemoCode] = useState(null); // only present in mock/demo env

  const requestReset = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setLoading(true);
    try {
      const res = await apiRequestPasswordReset(email.trim().toLowerCase());
      setDemoCode(res.demo_code || null); // shown only when provider=mock (no real SMTP)
      setStep(2);
      toast.success('Richiesta inviata', {
        description: res.demo_code
          ? 'Provider email non configurato — usa il codice mostrato sotto (DEMO).'
          : "Controlla la tua casella email per il codice di verifica.",
      });
    } catch (err) {
      toast.error("Impossibile inviare la richiesta. Riprova tra qualche istante.");
    } finally {
      setLoading(false);
    }
  };

  const confirmReset = async (e) => {
    e.preventDefault();
    if (newPwd.length < 8) { toast.error('La password deve essere di almeno 8 caratteri.'); return; }
    if (newPwd !== confirmPwd) { toast.error('Le password non coincidono.'); return; }
    setLoading(true);
    try {
      await apiConfirmPasswordReset(email.trim().toLowerCase(), code.trim(), newPwd);
      setStep(3);
      toast.success('Password aggiornata', { description: 'Ora puoi accedere con la nuova password.' });
    } catch (err) {
      const detail = err?.response?.data?.detail || 'reset_failed';
      if (detail === 'invalid_or_expired_code') toast.error('Codice non valido o scaduto.');
      else if (detail === 'too_many_attempts') toast.error('Troppi tentativi. Richiedi un nuovo codice.');
      else toast.error('Errore durante il reset. Riprova.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="tv-gradient-soft min-h-[80vh] py-14">
      <div className="mx-auto max-w-md px-4 sm:px-6">
        <div className="text-center mb-6">
          <img src={logo} alt="Tecnologie" className="h-10 mx-auto" />
          <div className="mt-1 text-[11px] uppercase tracking-widest text-[#0d63b0] font-semibold">Secure Push · Reset password</div>
        </div>

        <Card className="border border-slate-200 p-7">
          {step === 1 && (
            <>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-lg bg-blue-50 border border-blue-100 flex items-center justify-center">
                  <KeyRound className="h-5 w-5 text-[#0d63b0]" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-slate-900">Password dimenticata</h1>
                  <p className="text-sm text-slate-600">Ti inviamo un codice via email.</p>
                </div>
              </div>

              <form onSubmit={requestReset} className="mt-5 space-y-4">
                <div>
                  <Label>Email dell'account</Label>
                  <Input
                    data-testid="forgot-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="tu@azienda.it"
                    className="mt-1"
                  />
                </div>
                <Button data-testid="forgot-request-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Mail className="h-4 w-4 mr-1.5" /> Invia codice</>)}
                </Button>
              </form>

              <div className="mt-5 pt-5 border-t border-slate-100 text-sm text-slate-600 text-center">
                <Link to="/login" className="inline-flex items-center gap-1 font-semibold text-[#0d63b0] hover:underline">
                  <ArrowLeft className="h-3.5 w-3.5" /> Torna al login
                </Link>
              </div>
            </>
          )}

          {step === 2 && (
            <>
              <div className="flex items-center gap-2">
                <div className="h-10 w-10 rounded-lg bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                  <ShieldCheck className="h-5 w-5 text-emerald-600" />
                </div>
                <div>
                  <h1 className="text-2xl font-extrabold text-slate-900">Inserisci il codice</h1>
                  <p className="text-sm text-slate-600">Codice a 6 cifre inviato a <span className="font-mono">{email}</span></p>
                </div>
              </div>

              {demoCode && (
                <div className="mt-4 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <strong>DEMO / SMTP non configurato:</strong> nessun provider email attivo. Codice generato per test: <span className="font-mono text-base font-bold text-amber-800">{demoCode}</span>
                </div>
              )}

              <form onSubmit={confirmReset} className="mt-5 space-y-4">
                <div>
                  <Label>Codice di verifica</Label>
                  <Input
                    data-testid="forgot-code-input"
                    type="text"
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    maxLength={6}
                    required
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                    placeholder="000000"
                    className="mt-1 text-center text-2xl tracking-[0.5em] font-mono"
                  />
                </div>
                <div>
                  <Label>Nuova password</Label>
                  <Input
                    data-testid="forgot-newpwd-input"
                    type="password"
                    required
                    minLength={8}
                    value={newPwd}
                    onChange={(e) => setNewPwd(e.target.value)}
                    placeholder="Almeno 8 caratteri"
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>Conferma nuova password</Label>
                  <Input
                    data-testid="forgot-confirmpwd-input"
                    type="password"
                    required
                    value={confirmPwd}
                    onChange={(e) => setConfirmPwd(e.target.value)}
                    placeholder="Ripeti la password"
                    className="mt-1"
                  />
                </div>

                <Button data-testid="forgot-confirm-btn" type="submit" disabled={loading} className="w-full h-11 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                  {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><KeyRound className="h-4 w-4 mr-1.5" /> Reimposta password</>)}
                </Button>
              </form>

              <div className="mt-5 pt-5 border-t border-slate-100 text-sm text-slate-600 text-center flex items-center justify-between">
                <button
                  type="button"
                  onClick={() => { setStep(1); setCode(''); setDemoCode(null); }}
                  className="inline-flex items-center gap-1 text-slate-600 hover:text-slate-900"
                >
                  <ArrowLeft className="h-3.5 w-3.5" /> Cambia email
                </button>
                <button
                  type="button"
                  onClick={requestReset}
                  disabled={loading}
                  className="text-[#0d63b0] font-semibold hover:underline disabled:opacity-50"
                >
                  Invia nuovo codice
                </button>
              </div>
            </>
          )}

          {step === 3 && (
            <div className="text-center py-4">
              <div className="mx-auto h-14 w-14 rounded-full bg-emerald-100 text-emerald-600 flex items-center justify-center">
                <CheckCircle2 className="h-7 w-7" />
              </div>
              <h1 className="mt-4 text-2xl font-extrabold text-slate-900">Password aggiornata</h1>
              <p className="mt-2 text-sm text-slate-600">La tua password è stata reimpostata correttamente. Ora puoi accedere con le nuove credenziali.</p>
              <Button data-testid="forgot-goto-login-btn" onClick={() => nav('/login')} className="mt-6 w-full h-11 bg-[#0d63b0] hover:bg-[#0a4f8f] text-white">
                Vai al login
              </Button>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
